const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

// --- Graceful auto-reload after a new deploy ---------------------------------
// When a new version is deployed we don't reload immediately (that would yank the
// page out from under someone mid-task). Instead we mark an update as pending and
// reload only when it's safe: the tab is idle, nothing is being typed, no request
// is in flight, and no loader is showing. As a guaranteed floor, once the user's
// 12h login has elapsed we reload regardless.
const IDLE_MS = 20000;                    // 20s with no interaction = "idle"
const WATCH_MS = 30000;                   // re-check "safe to reload" every 30s
const UPDATE_CHECK_MS = 30 * 60 * 1000;   // ask the SW to check for a new version every 30 min
const SESSION_MS = 12 * 60 * 60 * 1000;   // login token lifetime (hard reload floor)

let updatePending = false;
let reloading = false;
let watcherStarted = false;
let lastActivity = Date.now();

function typingInField(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
}

function loaderVisible(): boolean {
  const el = document.getElementById('loader');
  return !!el && !el.classList.contains('d-none');
}

function activeRequests(): number {
  return (window as any).__activeRequests || 0;
}

function sessionExpired(): boolean {
  const t = localStorage.getItem('loginTime');
  if (!t) return false;
  const started = new Date(t).getTime();
  if (isNaN(started)) return false;
  return Date.now() - started >= SESSION_MS;
}

function safeToReload(): boolean {
  if (!updatePending || reloading) return false;
  if (sessionExpired()) return true;          // guaranteed 12h floor
  if (activeRequests() > 0) return false;     // never interrupt an in-flight request (e.g. AI analysis)
  if (typingInField()) return false;          // never interrupt typing
  if (loaderVisible()) return false;          // something is loading
  if (document.hidden) return true;           // tab not visible → unobtrusive
  if (Date.now() - lastActivity < IDLE_MS) return false; // user is active
  return true;
}

function maybeReload() {
  if (safeToReload()) {
    reloading = true;
    window.location.reload();
  }
}

function startReloadWatcher() {
  if (watcherStarted) return;
  watcherStarted = true;
  const mark = () => { lastActivity = Date.now(); };
  ['keydown', 'click', 'mousemove', 'scroll', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, mark, { passive: true })
  );
  window.setInterval(maybeReload, WATCH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) maybeReload();
  });
}

function registerValidSW(swUrl: string, config?: Config) {
  startReloadWatcher();

  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Periodically ask the browser to check for a new SW so long-open tabs
      // notice deploys without needing a navigation.
      window.setInterval(() => registration.update().catch(() => {}), UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) registration.update().catch(() => {});
      });

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // A real update (a controller already existed). Activate the new SW
              // and flag a pending reload — the watcher applies it when it's safe.
              registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
              updatePending = true;
              maybeReload();
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // First install (no prior controller) — fresh load, no reload needed.
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
